import { Delete, DriveFileRenameOutline, MoreVert } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Typography,
} from "@mui/material";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import RenameFileModal from "./RenameFileModal";
import { useToast } from "contexts/ToastContext";
import {
  useAdminDeleteProcedure,
  useAdminProceduresByPatient,
  useAdminRenameProcedure,
  useAdminRenameProcedureFile,
  useAdminToggleProcedureVisibility,
} from "hooks/patientDetail/useAdminProcedure";
import type {
  ProcedureGenerationOutputDto,
  RenameProcedureFileDtoFileTypeEnum,
} from "libs/cellera-sdk";
import React, { useCallback, useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  formatDateForFileDisplay,
  formatDateForFileName,
  formatDateTimeForDisplay,
  generateFileName,
  getFileIcon,
  handleFileDownload,
} from "utils/protocol-helpers";
import { handlePrintFile } from "utils/printHelpers";
import { isShareSupported, shareFile } from "utils/share";

import DeleteProtocolModal from "./DeleteProtocolModal";
import FileActionsMenu from "components/common/FileActionsMenu";

const openLinkInNewTab = (url: string | undefined | null): void => {
  if (url == null || url === "") return;
  window.open(url, "_blank", "noopener,noreferrer");
};

interface AdminProceduresProps {
  patientId: string;
  firstName: string;
  lastName: string;
}

interface ProcedureFile {
  type: "DOCX" | "PDF";
  url: string | undefined;
  fileName: string;
  apiFileType: RenameProcedureFileDtoFileTypeEnum;
}

interface Procedure {
  id: string;
  date: Date;
  displayLabel: string;
  files: ProcedureFile[];
  isHiddenFromPatient: boolean;
  customName?: string;
}

interface ToggleTarget {
  procedureId: string;
  nextIsHidden: boolean;
}

type MenuAnchorState = { el: HTMLElement; procedureId: string } | null;

interface ProcedureFileCardRowProps {
  file: ProcedureFile;
  procedureDate: Date;
  parentId: string;
  fileIndex: number;
  getFileIconColor: (type: "DOCX" | "PDF") => string;
  loadingFileKeys: Set<string>;
  onPrint: (fileKey: string, url: string | undefined, fileName: string) => void;
  onRenameFile: (
    fileName: string,
    apiFileType: RenameProcedureFileDtoFileTypeEnum,
  ) => void;
  onDownload: (
    fileKey: string,
    url: string | undefined,
    fileName: string,
  ) => void | Promise<void>;
  onShare: (
    fileKey: string,
    url: string | undefined,
    fileName: string,
  ) => void | Promise<void>;
}

function ProcedureFileCardRow({
  file,
  procedureDate,
  parentId,
  fileIndex,
  getFileIconColor,
  loadingFileKeys,
  onPrint,
  onRenameFile,
  onDownload,
  onShare,
}: ProcedureFileCardRowProps) {
  const { colors } = useCelleraTheme();
  const fileKey = `${parentId}-${fileIndex}`;
  const IconComponent = getFileIcon(file.type);
  const iconColor = getFileIconColor(file.type);
  const fileDate = formatDateForFileDisplay(procedureDate);
  const isLoadingFile = loadingFileKeys.has(fileKey);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        border: `1px solid ${colors.border.light}`,
        borderRadius: "8px",
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 48,
          height: 48,
          flexShrink: 0,
          bgcolor: colors.background.secondary,
          borderRadius: "4px",
        }}
      >
        <IconComponent sx={{ fontSize: 32, color: iconColor }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: colors.text.primary,
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          onClick={() => openLinkInNewTab(file.url)}
          className="cursor-pointer"
        >
          {file.fileName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            display: "block",
            mb: 0.5,
          }}
        >
          {fileDate}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            display: "block",
          }}
        >
          {file.type} Format
        </Typography>
      </Box>

      <FileActionsMenu
        fileName={file.fileName}
        isLoading={isLoadingFile}
        menuId={`${fileKey}-actions-menu`}
        onPrint={() => onPrint(fileKey, file.url, file.fileName)}
        onDownload={() => onDownload(fileKey, file.url, file.fileName)}
        onShare={
          file.url && isShareSupported(file.url)
            ? () => onShare(fileKey, file.url, file.fileName)
            : undefined
        }
      />
    </Box>
  );
}

interface ProcedureDocumentCardProps {
  item: Procedure;
  menuAnchor: MenuAnchorState;
  onMenuButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMenuClose: () => void;
  onDeleteFromMenu: () => void;
  onRenameFromMenu: () => void;
  onToggleVisibility: () => void;
  getFileIconColor: (type: "DOCX" | "PDF") => string;
  loadingFileKeys: Set<string>;
  onPrintFile: (
    fileKey: string,
    url: string | undefined,
    fileName: string,
  ) => void;
  onRenameFile: (
    fileName: string,
    apiFileType: RenameProcedureFileDtoFileTypeEnum,
  ) => void;
  onDownloadFile: (
    fileKey: string,
    url: string | undefined,
    fileName: string,
  ) => void | Promise<void>;
  onShareFile: (
    fileKey: string,
    url: string | undefined,
    fileName: string,
  ) => void | Promise<void>;
}

function ProcedureDocumentCard({
  item,
  menuAnchor,
  onMenuButtonClick,
  onMenuClose,
  onDeleteFromMenu,
  onRenameFromMenu,
  onToggleVisibility,
  getFileIconColor,
  loadingFileKeys,
  onPrintFile,
  onRenameFile,
  onDownloadFile,
  onShareFile,
}: ProcedureDocumentCardProps) {
  const { colors } = useCelleraTheme();
  const displayDateTime = formatDateTimeForDisplay(item.date);
  const isVisible = !item.isHiddenFromPatient;
  const isMenuOpen = !!menuAnchor && menuAnchor.procedureId === item.id;

  return (
    <Card
      sx={{
        mb: 2,
        border: `1px solid ${colors.border.light}`,
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ flex: 1, pb: "8px !important" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
                lineHeight: 1.4,
              }}
            >
              {item.displayLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.secondary }}>
              Created on {displayDateTime}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onMenuButtonClick}
            sx={{
              color: colors.text.secondary,
              flexShrink: 0,
              "&:hover": { bgcolor: colors.background.secondary },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchor?.el}
            open={isMenuOpen}
            onClose={onMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: { borderRadius: "8px", minWidth: 140 },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                onMenuClose();
                onRenameFromMenu();
              }}
            >
              <ListItemIcon>
                <DriveFileRenameOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Rename</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onMenuClose();
                onDeleteFromMenu();
              }}
              sx={{ color: colors.error[500] }}
            >
              <ListItemIcon>
                <Delete fontSize="small" sx={{ color: colors.error[500] }} />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {item.files.map((file, fileIndex) => (
            <ProcedureFileCardRow
              key={`${item.id}-${fileIndex}`}
              file={file}
              procedureDate={item.date}
              parentId={item.id}
              fileIndex={fileIndex}
              getFileIconColor={getFileIconColor}
              loadingFileKeys={loadingFileKeys}
              onPrint={onPrintFile}
              onRenameFile={onRenameFile}
              onDownload={onDownloadFile}
              onShare={onShareFile}
            />
          ))}
        </Box>
      </CardContent>

      <Box sx={{ px: 2, py: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isVisible}
              onChange={onToggleVisibility}
              size="small"
              color="primary"
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                color: isVisible ? colors.primary.main : colors.text.secondary,
                fontWeight: 500,
              }}
            >
              The patient can see this document
            </Typography>
          }
        />
      </Box>
    </Card>
  );
}

const AdminProcedures: React.FC<AdminProceduresProps> = ({
  patientId,
  firstName,
  lastName,
}) => {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [toggleTarget, setToggleTarget] = useState<ToggleTarget | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchorState>(null);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    currentName: string;
  } | null>(null);
  const [fileRenameTarget, setFileRenameTarget] = useState<{
    procedureId: string;
    fileType: RenameProcedureFileDtoFileTypeEnum;
    currentName: string;
  } | null>(null);
  const [loadingFileKeys, setLoadingFileKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const {
    procedures: proceduresData,
    pagination,
    isLoading,
  } = useAdminProceduresByPatient({
    patientId,
    page,
    pageSize,
  });

  const deleteMutation = useAdminDeleteProcedure(patientId);
  const toggleVisibilityMutation = useAdminToggleProcedureVisibility(patientId);
  const renameMutation = useAdminRenameProcedure(patientId);
  const fileRenameMutation = useAdminRenameProcedureFile(patientId);

  const abbreviation =
    firstName && lastName
      ? `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`
      : "XX";

  const procedures = useMemo<Procedure[]>(() => {
    const items: Procedure[] = [];

    proceduresData.forEach((procedure: ProcedureGenerationOutputDto) => {
      const dateStr = formatDateForFileName(procedure.createdAt);
      const date = new Date(procedure.createdAt);
      const fcn = procedure.fileCustomNames as
        | Record<string, string>
        | undefined;
      const procCustomName = procedure.customName
        ? String(procedure.customName)
        : undefined;

      const files: ProcedureFile[] = [];

      if (procedure.procedureTxtUrl) {
        files.push({
          type: "DOCX",
          url: procedure.procedureTxtUrl as unknown as string,
          fileName: fcn?.txt || generateFileName(abbreviation, dateStr, "docx"),
          apiFileType: "txt" as RenameProcedureFileDtoFileTypeEnum,
        });
      }

      if (procedure.procedurePdfUrl) {
        files.push({
          type: "PDF",
          url: procedure.procedurePdfUrl as unknown as string,
          fileName: fcn?.pdf || generateFileName(abbreviation, dateStr, "pdf"),
          apiFileType: "pdf" as RenameProcedureFileDtoFileTypeEnum,
        });
      }

      if (files.length > 0) {
        items.push({
          id: procedure.procedureId,
          date,
          displayLabel: procCustomName || `${abbreviation}${dateStr}`,
          files,
          isHiddenFromPatient: procedure.isHiddenFromPatient,
          customName: procCustomName,
        });
      }
    });

    items.sort((a, b) => b.date.getTime() - a.date.getTime());

    const dateGroups = new Map<string, Procedure[]>();
    items.forEach((item) => {
      const dateKey = formatDateForFileName(item.date);
      const group = dateGroups.get(dateKey);
      if (group) {
        group.push(item);
      } else {
        dateGroups.set(dateKey, [item]);
      }
    });

    const result: Procedure[] = [];
    dateGroups.forEach((group) => {
      if (group.length === 1) {
        // Single item per date - keep as-is (names already set correctly)
        result.push(group[0]);
      } else {
        group.forEach((item, index) => {
          const dateStr = formatDateForFileName(item.date);
          const suffix = index + 1;
          // Only add suffix to default-named files; preserve custom names
          const updatedFiles = item.files.map((file) => {
            const defaultName = generateFileName(
              abbreviation,
              dateStr,
              file.type.toLowerCase() as "docx" | "pdf",
            );
            if (file.fileName !== defaultName) return file;
            return {
              ...file,
              fileName: generateFileName(
                abbreviation,
                dateStr,
                file.type.toLowerCase() as "docx" | "pdf",
                suffix,
              ),
            };
          });
          result.push({
            ...item,
            displayLabel: item.customName
              ? item.displayLabel
              : `${abbreviation}${dateStr} (${suffix})`,
            files: updatedFiles,
          });
        });
      }
    });

    return result.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [proceduresData, abbreviation]);

  const addLoadingFileKey = useCallback((fileKey: string) => {
    setLoadingFileKeys((prev) => {
      const next = new Set(prev);
      next.add(fileKey);
      return next;
    });
  }, []);

  const removeLoadingFileKey = useCallback((fileKey: string) => {
    setLoadingFileKeys((prev) => {
      if (!prev.has(fileKey)) return prev;
      const next = new Set(prev);
      next.delete(fileKey);
      return next;
    });
  }, []);

  const handlePrintProcedureFile = useCallback(
    (fileKey: string, url: string | undefined, fileName: string) => {
      handlePrintFile(url, fileName, {
        onStart: () => addLoadingFileKey(fileKey),
        onComplete: () => removeLoadingFileKey(fileKey),
        onError: () => {
          removeLoadingFileKey(fileKey);
          showError(
            "Print Error",
            "Failed to print document. Opening in new tab instead.",
          );
        },
      });
    },
    [addLoadingFileKey, removeLoadingFileKey, showError],
  );

  const handleDownloadProcedureFile = useCallback(
    async (fileKey: string, url: string | undefined, fileName: string) => {
      if (!url) return;
      addLoadingFileKey(fileKey);
      try {
        await handleFileDownload(url, fileName);
      } finally {
        removeLoadingFileKey(fileKey);
      }
    },
    [addLoadingFileKey, removeLoadingFileKey],
  );

  const handleShareProcedureFile = useCallback(
    async (fileKey: string, url: string | undefined, fileName: string) => {
      if (!url) return;
      addLoadingFileKey(fileKey);
      try {
        await shareFile({ url, fileName });
      } catch (error) {
        console.error("[AdminProcedures] Failed to share file:", error);
      } finally {
        removeLoadingFileKey(fileKey);
      }
    },
    [addLoadingFileKey, removeLoadingFileKey],
  );

  const getFileIconColor = useCallback(
    (type: "DOCX" | "PDF") => {
      switch (type) {
        case "DOCX":
          return colors.info[500];
        case "PDF":
          return colors.error[500];
        default:
          return colors.text.secondary;
      }
    },
    [colors],
  );

  const handleToggleClick = (procedureId: string, currentIsHidden: boolean) => {
    setToggleTarget({ procedureId, nextIsHidden: !currentIsHidden });
  };

  const handleToggleConfirm = () => {
    if (!toggleTarget) return;
    toggleVisibilityMutation.mutate(
      {
        procedureId: toggleTarget.procedureId,
        isHiddenFromPatient: toggleTarget.nextIsHidden,
      },
      {
        onSettled: () => setToggleTarget(null),
      },
    );
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (procedures.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              fontStyle: "italic",
            }}
          >
            No procedures found for this patient
          </Typography>
        </Box>
      );
    }

    const totalPages = Math.ceil((pagination?.total || 0) / pageSize);

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
            },
            gap: 3,
          }}
        >
          {procedures.map((item) => (
            <ProcedureDocumentCard
              key={item.id}
              item={item}
              menuAnchor={menuAnchor}
              onMenuButtonClick={(e) =>
                setMenuAnchor({
                  el: e.currentTarget,
                  procedureId: item.id,
                })
              }
              onMenuClose={() => setMenuAnchor(null)}
              onDeleteFromMenu={() => setDeleteTarget(item.id)}
              onRenameFromMenu={() =>
                setRenameTarget({
                  id: item.id,
                  currentName: item.displayLabel,
                })
              }
              onToggleVisibility={() =>
                handleToggleClick(item.id, item.isHiddenFromPatient)
              }
              getFileIconColor={getFileIconColor}
              loadingFileKeys={loadingFileKeys}
              onPrintFile={handlePrintProcedureFile}
              onRenameFile={(fileName, apiFileType) =>
                setFileRenameTarget({
                  procedureId: item.id,
                  fileType: apiFileType,
                  currentName: fileName,
                })
              }
              onDownloadFile={handleDownloadProcedureFile}
              onShareFile={handleShareProcedureFile}
            />
          ))}
        </Box>

        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </>
    );
  };

  const toggleConfirmMessage = toggleTarget?.nextIsHidden
    ? "Are you sure you want to hide this procedure from the patient? The patient will no longer be able to see this document."
    : "Are you sure you want to make this procedure visible to the patient? The patient will be able to see this document.";

  return (
    <Box sx={{ p: 3 }}>
      {renderContent()}

      <DeleteProtocolModal
        open={!!deleteTarget}
        type="procedure"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget, {
            onSettled: () => setDeleteTarget(null),
          });
        }}
        loading={deleteMutation.isPending}
      />

      <ConfirmationPopup
        open={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={handleToggleConfirm}
        title={
          toggleTarget?.nextIsHidden
            ? "Hide from Patient"
            : "Make Visible to Patient"
        }
        message={toggleConfirmMessage}
        confirmText={toggleTarget?.nextIsHidden ? "Hide" : "Make Visible"}
        cancelText="Cancel"
        confirmColor={
          toggleTarget?.nextIsHidden ? colors.warning[600] : colors.primary.main
        }
        loading={toggleVisibilityMutation.isPending}
        maxWidth="sm"
        paperSx={{ borderRadius: 4 }}
      />

      <RenameFileModal
        open={!!renameTarget}
        currentName={renameTarget?.currentName ?? ""}
        onClose={() => setRenameTarget(null)}
        onConfirm={(newName) => {
          if (!renameTarget) return;
          renameMutation.mutate(
            { procedureId: renameTarget.id, customName: newName },
            { onSuccess: () => setRenameTarget(null) },
          );
        }}
        loading={renameMutation.isPending}
      />

      <RenameFileModal
        open={!!fileRenameTarget}
        currentName={fileRenameTarget?.currentName ?? ""}
        onClose={() => setFileRenameTarget(null)}
        onConfirm={(newName) => {
          if (!fileRenameTarget) return;
          fileRenameMutation.mutate(
            {
              procedureId: fileRenameTarget.procedureId,
              fileType: fileRenameTarget.fileType,
              customName: newName,
            },
            { onSuccess: () => setFileRenameTarget(null) },
          );
        }}
        loading={fileRenameMutation.isPending}
      />
    </Box>
  );
};

export default AdminProcedures;
